import { ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
	Alert,
	Box,
	Grid2,
	IconButton,
	LinearProgress,
	Paper,
	Stack,
	Typography,
	useMediaQuery,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import {
	Group,
	Panel,
	useDefaultLayout,
	useGroupRef,
	type Layout,
} from "react-resizable-panels";
import { useNavigate, useParams } from "react-router-dom";

import { CustomSeparator } from "@/pages/catalogs/shared/components/CustomSeparator";
import { MILL_1, type MillId } from "@/setup/mill-config/mill-config";
import { routeM1TestShackManager } from "@/setup/routes/products.routes";
import { useShiftInspectionQuery } from "../../hooks/queries/useShiftInspectionsQuery";
import type { SectionSpecDetail } from "@/shared/models/SectionSpec";
import { M1InspectionForm } from "../components/M1InspectionForm";
import { useSheetInspectionForm } from "./hooks/useSheetInspectionForm";
import type { InspectionFormSubmission } from "../../models/InspectionForm";
import { M1SectionDetailsPanel } from "../components/M1SectionDetailsPanel";
import type { UpsertInspectionPayload } from "../../models/Inspection";
import { useUpsertInspectionMutation } from "../../hooks/mutations/useInspectionMutations";

const FORM_PANEL_ID = "sheet-inspection";
const DETAILS_PANEL_ID = "sheet-section-details";
const SHEET_LAYOUT_ID = "m1-test-shack-sheet";
const DEFAULT_DESKTOP_LAYOUT: Layout = {
	[FORM_PANEL_ID]: 60,
	[DETAILS_PANEL_ID]: 40,
};

export const M1TestShackSheetPage = () => {
	const assetId = MILL_1.id;
	const { profileInspectionId } = useParams();
	return (
		<TestShackSheetPageContent
			key={`${assetId}:${profileInspectionId}`}
			assetId={assetId}
			profileInspectionId={Number(profileInspectionId) || null}
		/>
	);
};

interface ContentProps {
	assetId: MillId;
	profileInspectionId: number | null;
}

const TestShackSheetPageContent = ({
	assetId,
	profileInspectionId,
}: ContentProps) => {
	const navigate = useNavigate();
	// Below lg, the 744px form and the drawing cannot both fit side by side.
	const isDesktop = useMediaQuery((theme) => theme.breakpoints.up("lg"));
	const groupRef = useGroupRef();
	const { defaultLayout, onLayoutChanged } = useDefaultLayout({
		id: SHEET_LAYOUT_ID,
		panelIds: [FORM_PANEL_ID, DETAILS_PANEL_ID],
	});

	const [error, setError] = useState("");
	const [specDetail, setSpecDetail] = useState<SectionSpecDetail | null>(null);
	const submitInProgressRef = useRef(false);
	const onError = (queryError: Error) => setError(queryError.message);

	const {
		data,
		isLoading,
		error: e,
		isError,
	} = useShiftInspectionQuery({ assetId, profileInspectionId });
	const inspection = data?.inspections[0] ?? null;

	const spec = specDetail?.spec ?? null;

	const saveMutation = useUpsertInspectionMutation({ onError });

	const backToManager = () =>
		navigate(
			inspection?.ShiftId
				? `${routeM1TestShackManager}?mode=shift&shift=${inspection.ShiftId}`
				: routeM1TestShackManager,
		);

	const handleSubmit = (payload: InspectionFormSubmission) => {
		if (!inspection || submitInProgressRef.current || saveMutation.isPending)
			return;

		const snapshot: UpsertInspectionPayload = {
			profileInspectionId: inspection.ProfileInspectionId,
			assetId,
			sectionId: payload.sectionId,
			heatId: payload.heatId,
			inspectedAt: new Date(payload.inspectedAt),
			heatName: null,
			measuredLbsPerFoot: payload.measuredLbsPerFoot,
			weightProfileItemId: specDetail?.weightProfileItemId ?? null,
			comments: payload.comments,
			readings: payload.readings.map((reading) => ({ ...reading })),
		};

		submitInProgressRef.current = true;
		saveMutation.mutate(snapshot, {
			onSettled: () => {
				submitInProgressRef.current = false;
			},
		});
	};

	useEffect(() => {
		if (e && isError) {
			setError(e.message);
		}
	}, [isError, e]);

	// Keep the draft above responsive branches so resizing cannot discard edits.
	const inspectionForm = useSheetInspectionForm({
		assetId,
		onSpecChange: setSpecDetail,
		editing: inspection,
		saving: saveMutation.isPending,
		onCancelEdit: backToManager,
		onSubmit: handleSubmit,
	});
	const formPanel = (
		<Box sx={{ height: "100%", overflow: "auto" }}>
			{inspection && <M1InspectionForm layout="sheet" {...inspectionForm} />}
		</Box>
	);
	const detailsPanel = (
		<Paper elevation={2} sx={{ p: 2, height: "100%", overflow: "auto" }}>
			<M1SectionDetailsPanel
				sectionId={inspection?.SectionId ?? null}
				spec={spec}
			/>
		</Paper>
	);

	return (
		<>
			<HelmetDexteel
				title={
					inspection
						? `${inspection.SectionName} - Test Shack Sample`
						: "Test Shack Sample"
				}
			/>
			<Box
				sx={{
					boxSizing: "border-box",
					display: "grid",
					gap: 1,
					gridTemplateRows: "auto minmax(0, 1fr)",
					height: "calc(100vh - 64px)",
					px: 2,
					py: 2,
				}}
			>
				<Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
					<IconButton
						size="small"
						title="Back to the manager"
						aria-label="Back to the manager"
						onClick={backToManager}
					>
						<ArrowBackIcon fontSize="small" />
					</IconButton>
					<Typography variant="h5" fontWeight={600}>
						{inspection
							? `#${inspection.ProfileInspectionId} · ${inspection.SectionName}`
							: "Test Shack Sample"}
					</Typography>
				</Stack>

				<Box sx={{ minHeight: 0, overflow: "auto" }}>
					{isLoading && <LinearProgress aria-label="Loading the sample" />}
					{isError && (
						<Alert severity="error" role="alert">
							The sample could not be loaded.
						</Alert>
					)}
					{!isLoading && !isError && inspection === null && (
						<Alert severity="info" role="status">
							This sample no longer exists.
						</Alert>
					)}

					{inspection &&
						(isDesktop ? (
							<Group
								id={SHEET_LAYOUT_ID}
								groupRef={groupRef}
								orientation="horizontal"
								defaultLayout={defaultLayout ?? DEFAULT_DESKTOP_LAYOUT}
								onLayoutChanged={onLayoutChanged}
								style={{ height: "100%" }}
							>
								<Panel id={FORM_PANEL_ID} minSize="744px">
									{formPanel}
								</Panel>
								<CustomSeparator
									onDoubleClick={() =>
										groupRef.current?.setLayout(DEFAULT_DESKTOP_LAYOUT)
									}
								/>
								<Panel id={DETAILS_PANEL_ID} minSize="20%">
									{detailsPanel}
								</Panel>
							</Group>
						) : (
							<Grid2 container spacing={1}>
								<Grid2 size={12}>{formPanel}</Grid2>
								<Grid2 size={12}>{detailsPanel}</Grid2>
							</Grid2>
						))}
				</Box>
			</Box>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
